import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Login from "./components/Login";
import Register from "./components/Register";
import { AuthProvider } from "./context/AuthContext";
import PrivateRoute from "./components/PrivateRoute"; // Import the PrivateRoute component
import navitems from "./navitems.json";
import Dashboard from "./pages/Dashboard";
import ApplicationForm from "./pages/ApplicationForm";
import LoanProcessing from "./pages/LoanProcessing";
import AddRepayment from "./pages/AddRepayment";
import DailyLoanRepaymentsDetailed from "./pages/DailyLoanRepaymentsDetailed";
import DailyLoanReleasesDetailed from "./pages/DailyLoanReleasesDetailed";
import LoansOutstandingBalanceSheet from "./pages/LoansOutstandingBalanceSheet";
import PastDueAccountsByAccountOfficer from "./pages/PastDueAccountsByAccountOfficer";
import OutstandingBalanceReportPerProduct from "./pages/OutstandingBalanceReportPerProduct";
import OutstandingBalanceReportPerAccountOfficer from "./pages/OutstandingBalanceReportPerAccountOfficer";
import DelinquentAccountReport from "./pages/DelinquentAccountReport";
import OutstandingBalanceReport from "./pages/OutstandingBalanceReport";
import DailyProofsheet from "./pages/DailyProofsheet";
import IncomeStatement from "./pages/IncomeStatement";
import BalanceSheet from "./pages/BalanceSheet";
import TrialBalance from "./pages/TrialBalance";
import LoanListingByLoanStatus from "./pages/LoanListingByLoanStatus";
import PortfolioAtRiskByAging from "./pages/PortfolioAtRiskByAging";
import AmortizationSchedule from "./pages/AmortizationSchedule";
import DisclosureDiscountStatement from "./pages/DisclosureDiscountStatement";
import AuthorityToDeduct from "./pages/AuthorityToDeduct";
import CoMakerStatement from "./pages/CoMakerStatement";
import PromissoryNote from "./pages/PromissoryNote";
import Vouchers from "./pages/Vouchers";
import PersonalConsentAndDeclarationOfBeneficiaryForMortuaryFundContributionProgram from "./pages/PersonalConsentAndDeclarationOfBeneficiaryForMortuaryFundContributionProgram";
import AffidavitIfConsentAndConformityToPPLCsContractualSavingsProgram from "./pages/AffidavitIfConsentAndConformityToPPLCsContractualSavingsProgram";
import AffidavitOfEmployment from "./pages/AffidavitOfEmployment";
import AffidavitOfConfirmationOfATMCardAsAPayrollAccount from "./pages/AffidavitOfConfirmationOfATMCardAsAPayrollAccount";
import AuthorizationForBankAccountInquiry from "./pages/AuthorizationForBankAccountInquiry";
import PastdueAccountFirstNotice from "./pages/PastdueAccountFirstNotice";
import PastdueAccountSecondNotice from "./pages/PastdueAccountSecondNotice";
import RePastdueAccountFinalNoticeBeforeLegalAction from "./pages/RePastdueAccountFinalNoticeBeforeLegalAction";
import DelinquentAccountFirstNotice from "./pages/DelinquentAccountFirstNotice";
import DemandLetter from "./pages/DemandLetter";
import ApplicantPage from "./pages/ApplicantPage";
import LoanPage from "./pages/LoanPage";
import PublicLoanPage from "./pages/PublicLoanPage";
import AddUserForm from "./pages/AddUserForm";
import ImportApplicant from "./pages/ImportApplicant";
import ImportLoanProcessing from "./pages/ImportLoanProcessing";
import ImportLoanPayments from "./pages/ImportLoanPayments";
import AssetManagement from "./pages/AssetManagement";
import LiabilityManagement from "./pages/LiabilityManagement";
import IncomeManagement from "./pages/IncomeManagement";
import ExpenseManagement from "./pages/ExpenseManagement";
import EquityManagement from "./pages/EquityManagement";
import InvestmentPayable from "./pages/InvestmentPayable";
import LoanApplication from "./pages/LoanApplication";
import LoanApplicationPage from "./pages/LoanApplicationPage";
import MessageTemplatesPage from "./pages/MessageTemplatesPage";
import NotFoundPage from "./pages/NotFoundPage";

// Component map for dynamic routing
const componentMap = {
    Dashboard,
    ApplicationForm,
    LoanProcessing,
    AddRepayment,
    DailyLoanRepaymentsDetailed,
    DailyLoanReleasesDetailed,
    LoansOutstandingBalanceSheet,
    PastDueAccountsByAccountOfficer,
    OutstandingBalanceReportPerProduct,
    OutstandingBalanceReportPerAccountOfficer,
    DelinquentAccountReport,
    OutstandingBalanceReport,
    DailyProofsheet,
    IncomeStatement,
    BalanceSheet,
    TrialBalance,
    LoanListingByLoanStatus,
    PortfolioAtRiskByAging,
    AmortizationSchedule,
    DisclosureDiscountStatement,
    AuthorityToDeduct,
    CoMakerStatement,
    PromissoryNote,
    Vouchers,
    PersonalConsentAndDeclarationOfBeneficiaryForMortuaryFundContributionProgram,
    AffidavitIfConsentAndConformityToPPLCsContractualSavingsProgram,
    AffidavitOfEmployment,
    AffidavitOfConfirmationOfATMCardAsAPayrollAccount,
    AuthorizationForBankAccountInquiry,
    PastdueAccountFirstNotice,
    PastdueAccountSecondNotice,
    RePastdueAccountFinalNoticeBeforeLegalAction,
    DelinquentAccountFirstNotice,
    DemandLetter,
    ApplicantPage,
    LoanPage,
    AssetManagement,
    LiabilityManagement,
    IncomeManagement,
    ExpenseManagement,
    EquityManagement,
    MessageTemplatesPage,
    InvestmentPayable,
};

const pageMetadata = {
    "dashboard": {
        title: "Dashboard - Hayahay Lending Corp",
        description: "Manage your lending operations efficiently. Monitor loan applications, track payments, and view key performance metrics.",
        keywords: "lending dashboard, loan management, financial metrics, payment tracking"
    },
    "loan-application": {
        title: "Online Loan Application - Quick and Easy Process | Hayahay Lending",
        description: "Apply for a personal loan online in minutes with Hayahay Lending Corp. Fast approval, competitive rates, and flexible terms for Filipinos. No collateral needed for qualified applicants.",
        keywords: "loan application, online loan, quick loan, personal loan, business loan, lending philippines, fast approval, hayahay lending",
        ogImage: "/logo-square.png",
        ogType: "website",
        twitterCard: "summary_large_image",
        structuredData: {
            "@context": "https://schema.org",
            "@type": "LoanOrCredit",
            "name": "Hayahay Lending Online Loan Application",
            "description": "Apply for a personal loan online in minutes with Hayahay Lending Corp.",
            "offers": {
                "@type": "Offer",
                "priceCurrency": "PHP",
                "availableAtOrFrom": {
                    "@type": "Place",
                    "name": "Hayahay Lending Corp",
                    "address": {
                        "@type": "PostalAddress",
                        "addressCountry": "Philippines"
                    }
                }
            },
            "provider": {
                "@type": "Organization",
                "name": "Hayahay Lending Corp",
                "logo": "https://app.hayahaycorp.com/logo-square.png"
            }
        }
    },
    "loan-application-page": {
        title: "Loan Applications Management - Hayahay Lending Corp",
        description: "Comprehensive loan applications management system. Review applications, track status, and process approvals efficiently.",
        keywords: "loan management, application tracking, loan processing, lending system"
    },
    "message-templates": {
        title: "Message Templates - Hayahay Lending Corp",
        description: "Manage communication templates for loan notifications, payment reminders, and customer updates.",
        keywords: "message templates, loan notifications, payment reminders, customer communication"
    },
    "login": {
        title: "Secure Login - Hayahay Lending Corp",
        description: "Access your Hayahay Lending account securely. Manage your loans, track payments, and access financial services.",
        keywords: "login, secure access, account login, lending portal, financial services login",
        ogImage: "https://app.hayahaycorp.com/images/login-og.jpg"
    },
    "404": {
        title: "Page Not Found - Hayahay Lending Corp",
        description: "The page you're looking for cannot be found. Please check the URL or return to our homepage.",
        keywords: "page not found, error page, 404, hayahay lending",
        ogImage: "https://app.hayahaycorp.com/images/404-og.jpg"
    }
};

const getPreloadedMetadata = (path) => {
    return pageMetadata[path] || null;
};

// Optimize the DynamicTitle component
const DynamicTitle = ({ title, path }) => {
    // Memoize metadata to prevent unnecessary re-renders
    const metadata = useMemo(() => {
        return pageMetadata[path] || {
            title: `${title} - Hayahay Lending Corp`,
            description: `${title} management and processing system by Hayahay Lending Corp.`,
            keywords: `${title.toLowerCase()}, lending, loan management, financial services`
        };
    }, [path, title]);

    // Memoize updateMetaTag function
    const updateMetaTag = useMemo(() => (name, content) => {
        let element = document.querySelector(`meta[property="${name}"]`) ||
                     document.querySelector(`meta[name="${name}"]`);
        
        if (!element) {
            element = document.createElement('meta');
            element.setAttribute(name.includes('og:') ? 'property' : 'name', name);
            document.head.appendChild(element);
        }
        element.setAttribute('content', content);
    }, []);

    useEffect(() => {
        // Update document head
        document.title = metadata.title;
        
        // Update Open Graph tags
        updateMetaTag('og:title', metadata.title);
        updateMetaTag('og:description', metadata.description);
        updateMetaTag('og:url', `https://app.hayahaycorp.com/${path}`);
        updateMetaTag('og:image', metadata.ogImage || 'https://app.hayahaycorp.com/logo-square.png');
        updateMetaTag('og:image:width', '1200');
        updateMetaTag('og:image:height', '630');
        updateMetaTag('og:locale', 'en_US');
        
        // Add structured data if available
        if (metadata.structuredData) {
            let scriptTag = document.querySelector('#structured-data-script');
            if (!scriptTag) {
                scriptTag = document.createElement('script');
                scriptTag.id = 'structured-data-script';
                scriptTag.type = 'application/ld+json';
                document.head.appendChild(scriptTag);
            }
            scriptTag.textContent = JSON.stringify(metadata.structuredData);
        } else if (document.querySelector('#structured-data-script')) {
            document.querySelector('#structured-data-script').remove();
        }

        return () => {
            // Cleanup if needed
        };
    }, [metadata, path, updateMetaTag]);

    return (
        <Helmet>
            <title>{metadata.title}</title>
            <meta name="description" content={metadata.description} />
            <meta name="keywords" content={metadata.keywords} />
            
            {/* Open Graph / Facebook */}
            <meta property="og:title" content={metadata.title} />
            <meta property="og:description" content={metadata.description} />
            <meta property="og:image" content={metadata.ogImage || 'https://app.hayahaycorp.com/logo-square.png'} />
            <meta property="og:url" content={`https://app.hayahaycorp.com/${path}`} />
            <meta property="og:type" content={metadata.ogType || 'website'} />
            <meta property="og:site_name" content="Hayahay Lending Corp" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:locale" content="en_US" />
            
            {/* Twitter */}
            <meta name="twitter:card" content={metadata.twitterCard || 'summary_large_image'} />
            <meta name="twitter:title" content={metadata.title} />
            <meta name="twitter:description" content={metadata.description} />
            <meta name="twitter:image" content={metadata.ogImage || 'https://app.hayahaycorp.com/logo-square.png'} />
            <meta name="twitter:site" content="@HayahayLending" />
            
            {/* Canonical and robots */}
            <link rel="canonical" href={`https://app.hayahaycorp.com/${path}`} />
            <meta name="robots" content="index, follow" />
            
            {/* Additional SEO tags */}
            <meta name="author" content="Hayahay Lending Corp" />
            <meta name="geo.region" content="PH" />
            <meta name="geo.placename" content="Philippines" />
            <meta name="application-name" content="Hayahay Lending App" />
            <meta name="apple-mobile-web-app-title" content="Hayahay Lending" />
        </Helmet>
    );
};

function CustomRoute() {
    return (
        <HelmetProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AuthProvider>
                    <Routes>
                        {/* Public Routes */}
                        <Route 
                            path="/login" 
                            element={
                                <>
                                    <DynamicTitle 
                                        title="Secure Login" 
                                        path="login"
                                    />
                                    <Login />
                                </>
                            } 
                        />
                        <Route 
                            path="/loan/:timestamp/:loanId" 
                            element={
                                <>
                                    <DynamicTitle 
                                        title="Loan Details" 
                                        path="loan"
                                    />
                                    <PublicLoanPage />
                                </>
                            } 
                        />
                        <Route 
                            path="*" 
                            element={
                                <>
                                    <DynamicTitle 
                                        title="Page Not Found" 
                                        path="404"
                                    />
                                    <NotFoundPage />
                                </>
                            } 
                        />
                        <Route 
                            path="/loan-application" 
                            element={
                                <>
                                    <DynamicTitle 
                                        title="Online Loan Application" 
                                        path="loan-application"
                                    />
                                    <LoanApplication />
                                </>
                            } 
                        />
                        <Route path="/loan-application-page" element={
                                <PrivateRoute>
                                    <DynamicTitle title="Applications" path="loan-application-page" />
                                    <LoanApplicationPage />
                                </PrivateRoute>
                            } />

                        {/* Protected Routes */}
                        <Route
                            path="/"
                            element={
                                <PrivateRoute>
                                    <DynamicTitle title="Dashboard" path="/dashboard" />
                                    <Dashboard />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/register"
                            element={
                                <PrivateRoute>
                                    <Register />
                                </PrivateRoute>
                            }
                        />
                        {navitems.map((item, index) => {
                            if (item.slug !== null && item.comp) {
                                const CustomComponent = componentMap[item.comp];
                                return (
                                    <Route
                                        path={`/${item.slug}`}
                                        key={index}
                                        element={
                                            <PrivateRoute>
                                                <DynamicTitle 
                                                    title={item.name}
                                                    path={item.slug}
                                                />
                                                <CustomComponent />
                                            </PrivateRoute>
                                        }
                                    />
                                );
                            }
                            return item.subitem?.map((subitem, subIndex) => {
                                if (subitem.slug !== null && subitem.comp) {
                                    const SubCustomComponent = componentMap[subitem.comp];
                                    return (
                                        <Route
                                            path={`/${subitem.slug}`}
                                            key={subIndex}
                                            element={
                                                <PrivateRoute>
                                                    <DynamicTitle title={subitem.name} path={`/${subitem.slug}`} />
                                                    <SubCustomComponent />
                                                </PrivateRoute>
                                            }
                                        />
                                    );
                                }
                                return null;
                            });
                        })}
                        <Route
                            path="/users/add"
                            element={
                                <PrivateRoute>
                                    <AddUserForm />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/applicant/:id"
                            element={
                                <PrivateRoute>
                                    <ApplicantPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/loans/:id"
                            element={
                                <PrivateRoute>
                                    <LoanPage />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/ImportApplicant"
                            element={
                                <PrivateRoute>
                                    <ImportApplicant />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/ImportLoanProcessing"
                            element={
                                <PrivateRoute>
                                    <ImportLoanProcessing />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/ImportLoanPayments"
                            element={
                                <PrivateRoute>
                                    <ImportLoanPayments />
                                </PrivateRoute>
                            }
                        />
                        <Route
                            path="/message-templates"
                            element={
                                <PrivateRoute>
                                    <DynamicTitle 
                                        title="Message Templates" 
                                        path="message-templates"
                                    />
                                    <MessageTemplatesPage />
                                </PrivateRoute>
                            }
                        />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default CustomRoute;
export { getPreloadedMetadata };